import { ReadOnlyField } from "@/components/ReadOnlyField";
import {
  ADDRESS_COLUMNS,
  TWO_COLUMNS,
} from "@/components/submissions/wc/grids";
import type { AddressValue } from "@/lib/address";
import { formatIsoDate } from "@/lib/displayFormat";
import { ENTITY_TYPE_LABELS } from "@/lib/entityTypes";
import { usStateLabel } from "@/lib/usStates";
import type { WcBusiness } from "@/lib/submissions/wc/business";

// What the WC wizard shows read-only, as its fields show it (BRK-S349): an
// insured on record on Basic information and in Preview submission, and an
// address on record or in the preview. Each value sits where its field sits
// on its page.

/** The insured, in Basic information's grid and order, without the industry
 *  it no longer asks (BRK-S388). The date the business started shows under
 *  the manifest's label, when it gives one: m/d/yyyy (D-10 #2). */
export function BusinessReadOnly({
  business: b,
  startDateLabel,
}: {
  business: WcBusiness;
  startDateLabel?: string;
}) {
  return (
    <dl className={TWO_COLUMNS}>
      <ReadOnlyField label="Legal name" value={b.legalName} full />
      <ReadOnlyField label="DBA name" value={b.dbaName} full />
      <ReadOnlyField label="State" value={b.state && usStateLabel(b.state)} />
      <ReadOnlyField label="FEIN" value={b.ein} />
      <ReadOnlyField
        label="Legal entity"
        value={b.entityType ? ENTITY_TYPE_LABELS[b.entityType] : ""}
      />
      <ReadOnlyField label="Business email" value={b.emailAddress} />
      <ReadOnlyField label="Business phone" value={b.phone} />
      {startDateLabel && (
        <ReadOnlyField
          label={startDateLabel}
          value={b.businessStartDate && formatIsoDate(b.businessStartDate)}
        />
      )}
    </dl>
  );
}

/** An address, laid out as `AddressFieldset` lays its fields. A second line
 *  shows only when there is one: it continues the street. */
export function AddressReadOnly({ address: a }: { address: AddressValue }) {
  return (
    <dl className={ADDRESS_COLUMNS}>
      <ReadOnlyField label="Street" value={a.street} full />
      {a.addressLine2 && (
        <ReadOnlyField label="Address line 2" value={a.addressLine2} full />
      )}
      <ReadOnlyField label="City" value={a.city} />
      <ReadOnlyField label="County" value={a.county} />
      <ReadOnlyField label="State" value={a.state && usStateLabel(a.state)} />
      <ReadOnlyField label="ZIP" value={a.zip} />
    </dl>
  );
}
